/**
 * Autentificare, din partea clientului.
 *
 * Sesiunea NU e tinuta in `localStorage` si nici in memoria SPA-ului: e un cookie `HttpOnly`, pe
 * care JavaScriptul nu-l poate citi. Deci „sunt autentificat?" nu e o stare locala, ci un raspuns
 * al serverului — `GET /api/eu`. Asta inseamna ca nu poate exista dezacord intre ce crede
 * interfata si ce accepta API-ul, care e cel mai enervant fel de bug de autentificare.
 */

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { api } from '../lib/api.ts';
import { chei, esteCheiaEu } from '../lib/query-keys.ts';

export interface RaspunsLogin {
  id: number;
  email: string;
  displayName: string;
  role: 'viewer' | 'operator' | 'admin' | 'service';
  mustChangePassword: boolean;
  expiresAt: number;
}

export interface DateLogin {
  email: string;
  parola: string;
}

/**
 * Sterge datele utilizatorului anterior si RECITESTE cine sunt.
 *
 * Ordinea si alegerea metodelor conteaza, si au costat un bug in productie:
 *
 *   - `queryClient.clear()` pare varianta evidenta, dar sterge si interogarea `eu`, si intreg
 *     `MutationCache`-ul — inclusiv mutatia de login din care e apelat. Observatorul lui `eu` din
 *     `Shell` ramane atunci blocat pe ultima stare cunoscuta (eroarea de dinainte de login), deci
 *     autentificarea reuseste, cookie-ul se pune, iar ecranul de login NU pleaca.
 *   - `invalidateQueries` pe o cheie care tocmai a fost stearsa nu are ce reinteroga.
 *
 * Deci: se sterge tot IN AFARA de `eu`, iar `eu` se reinteroga explicit. Dupa asta `Shell` afla
 * adevarul de la server — fie ca e login, fie ca e logout.
 */
async function reseteazaSesiunea(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  queryClient.removeQueries({ predicate: (interogare) => !esteCheiaEu(interogare.queryKey) });
  await queryClient.refetchQueries({ queryKey: chei.eu() });
}

/**
 * Login.
 *
 * La reusita se sterge cache-ul celuilalt utilizator: daca inainte fusese logat altcineva pe
 * acelasi browser, datele lui nu au voie sa ramana pe ecran nici o clipa.
 */
export function useLogin(): UseMutationResult<RaspunsLogin, Error, DateLogin> {
  const queryClient = useQueryClient();
  return useMutation<RaspunsLogin, Error, DateLogin>({
    mutationKey: chei.mutatii.login(),
    mutationFn: (date) => api.post<RaspunsLogin>('/auth/login', date),
    // Fara reincercare: un 401 nu se repara repetand, iar un 429 inseamna ca deja am incercat
    // prea des. `networkMode: 'always'` ca formularul sa dea un mesaj imediat cand nu e retea,
    // in loc sa para blocat.
    retry: false,
    networkMode: 'always',
    onSuccess: async () => {
      await reseteazaSesiunea(queryClient);
    },
  });
}

/**
 * Logout.
 *
 * Curatarea se face la final, MEREU — si pe calea de eroare. Daca serverul n-a raspuns, omul tot
 * vrea sa nu mai vada cifrele pe ecran; iar `eu` se reinteroga si va spune adevarul: daca sesiunea
 * chiar a murit, vine 401 si apare login-ul; daca nu, aplicatia ramane, ceea ce e corect.
 */
export function useLogout(): UseMutationResult<{ ok: boolean }, Error, void> {
  const queryClient = useQueryClient();
  return useMutation<{ ok: boolean }, Error, void>({
    mutationKey: chei.mutatii.logout(),
    mutationFn: () => api.post<{ ok: boolean }>('/auth/logout'),
    retry: false,
    networkMode: 'always',
    onSettled: async () => {
      await reseteazaSesiunea(queryClient);
    },
  });
}

export interface RaspunsStepUp {
  stepupUntil: number;
}

/**
 * Re-autentificare pentru afisarea unei parole bancare.
 *
 * Deschide o fereastra scurta legata de sesiunea curenta. Nu se pune in cache si nu se reincearca:
 * e o dovada de prezenta, iar o dovada repetata automat n-ar mai dovedi prezenta.
 */
export function useStepUp(): UseMutationResult<RaspunsStepUp, Error, { parola: string }> {
  const queryClient = useQueryClient();
  return useMutation<RaspunsStepUp, Error, { parola: string }>({
    mutationKey: chei.mutatii.stepUp(),
    mutationFn: (date) => api.post<RaspunsStepUp>('/auth/step-up', date),
    retry: false,
    networkMode: 'always',
    onSuccess: () => {
      // `/api/eu` poarta `stepupUntil`, deci interfata stie cand sa nu mai ceara parola.
      void queryClient.invalidateQueries({ queryKey: chei.eu() });
    },
  });
}

export interface DateSchimbaParola {
  parolaCurenta: string;
  parolaNoua: string;
}

export function useSchimbaParola(): UseMutationResult<{ ok: boolean }, Error, DateSchimbaParola> {
  const queryClient = useQueryClient();
  return useMutation<{ ok: boolean }, Error, DateSchimbaParola>({
    mutationKey: chei.mutatii.schimbaParola(),
    mutationFn: (date) => api.put<{ ok: boolean }>('/auth/parola', date),
    retry: false,
    networkMode: 'always',
    onSuccess: () => {
      // `mustChangePassword` devine `false`, deci ecranul de schimbare dispare.
      void queryClient.invalidateQueries({ queryKey: chei.eu() });
    },
  });
}
